import { useState } from "react";
import { ShieldCheck, ShieldOff, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SecurityManager() {
  const { user, checkAuth } = useAuth();
  const enabled = !!user?.mfa_enabled;
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [disabling, setDisabling] = useState(false);
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const enable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/mfa/enable", { code }); setRecovery(data.recovery_codes); setSetup(null); setCode(""); await checkAuth(); toast.success("Two-step verification enabled"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const disable = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/mfa/disable", { code }); setDisabling(false); setCode(""); await checkAuth(); toast.success("Two-step verification disabled"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="security-manager" className="max-w-2xl">
      <div className="mb-6"><h1 className="font-serif text-3xl font-bold text-navy">Security</h1><p className="mt-1 text-sm text-slate-500">Protect your account with two-step verification (an authenticator app).</p></div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-full ${enabled ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{enabled ? <ShieldCheck size={22} /> : <ShieldOff size={22} />}</div>
          <div><div className="font-semibold text-navy">Multi-factor authentication</div><div className="text-sm text-slate-500" data-testid="mfa-status">{enabled ? "Enabled — you’ll be asked for a code at sign-in." : "Not enabled."}</div></div>
        </div>

        {!enabled && !setup && !recovery && (
          <button onClick={startSetup} disabled={busy} data-testid="mfa-start-btn" className="btn-gold mt-5 text-sm"><KeyRound size={15} /> Enable two-step verification</button>
        )}

        {setup && (
          <form onSubmit={enable} className="mt-6 space-y-4 border-t border-slate-100 pt-5">
            <p className="text-sm text-slate-600">1. Scan this QR code with Google Authenticator, Microsoft Authenticator or similar.</p>
            <img src={setup.qr} alt="MFA QR code" data-testid="mfa-qr" className="h-44 w-44 rounded-lg border border-slate-200" />
            <p className="text-xs text-slate-500">Or enter this key manually: <code className="rounded bg-slate-100 px-1.5 py-0.5">{setup.secret}</code></p>
            <div><Label>2. Enter the 6-digit code to confirm</Label><Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-enable-code" inputMode="numeric" placeholder="123456" className="mt-1 max-w-[180px] tracking-widest" required /></div>
            <button type="submit" disabled={busy} data-testid="mfa-enable-btn" className="btn-gold text-sm">Confirm &amp; enable</button>
          </form>
        )}

        {recovery && (
          <div className="mt-6 border-t border-slate-100 pt-5" data-testid="mfa-recovery">
            <p className="text-sm font-medium text-navy">Save your recovery codes</p>
            <p className="mb-3 text-xs text-slate-500">Each code works once if you lose your authenticator. Store them somewhere safe.</p>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-4 font-mono text-sm sm:grid-cols-5">
              {recovery.map((c) => <div key={c} className="text-navy">{c}</div>)}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(recovery.join("\n")); toast.success("Copied"); }} className="btn-navy mt-3 text-xs"><Copy size={13} /> Copy codes</button>
            <button onClick={() => setRecovery(null)} className="ml-2 mt-3 text-xs text-slate-400 hover:text-navy">Done</button>
          </div>
        )}

        {enabled && !disabling && (
          <button onClick={() => setDisabling(true)} data-testid="mfa-disable-start" className="mt-5 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">Disable two-step verification</button>
        )}
        {disabling && (
          <form onSubmit={disable} className="mt-5 space-y-3 border-t border-slate-100 pt-5">
            <div><Label>Enter a current code (or a recovery code) to disable</Label><Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="mfa-disable-code" placeholder="123456" className="mt-1 max-w-[180px] tracking-widest" required /></div>
            <button type="submit" disabled={busy} data-testid="mfa-disable-btn" className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white">Disable</button>
            <button type="button" onClick={() => { setDisabling(false); setCode(""); }} className="ml-2 text-xs text-slate-400">Cancel</button>
          </form>
        )}
      </div>
    </div>
  );
}
